import React, { useState } from "react";
import axios from "axios";
import { PageHero } from "@/components/sections/Shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Phone, Mail, Clock, Loader2, Send } from "lucide-react";
import { BUSINESS } from "@/lib/business";
import SEO, { LOCAL_BUSINESS_JSONLD } from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill name, email, and message.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API}/contact`, form);
            toast.success(res.data?.message || "Message sent!");
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Could not send message. Please try again or call us."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Contact GTA Secure Inc."
                description={`Call ${BUSINESS.phone} or message us online. GTA Secure Inc. serves the Greater Toronto Area from ${BUSINESS.address.full}.`}
                path="/contact"
                keywords="contact GTA Secure, window film quote, security camera quote, blinds quote Toronto"
                jsonLd={LOCAL_BUSINESS_JSONLD}
            />
            <PageHero
                eyebrow="Contact Us"
                title="Let's talk about your project."
                subtitle="Call, email, or drop us a message — we respond quickly and never use pressure tactics."
            />

            <section className="section bg-[#FDFCFB]">
                <div className="container-x grid lg:grid-cols-[1fr_1.1fr] gap-12">
                    {/* Info */}
                    <div className="space-y-6">
                        <InfoItem
                            icon={Phone}
                            label="Phone"
                            value={BUSINESS.phone}
                            href={BUSINESS.phoneHref}
                            testid="contact-info-phone"
                        />
                        <InfoItem
                            icon={Mail}
                            label="Email"
                            value={BUSINESS.email}
                            href={`mailto:${BUSINESS.email}`}
                            testid="contact-info-email"
                        />
                        <InfoItem
                            icon={Clock}
                            label="Hours"
                            value="Mon–Sat · 9:00 AM – 7:00 PM"
                            testid="contact-info-hours"
                        />

                        <div className="rounded-2xl overflow-hidden border border-gray-100 mt-4 aspect-[4/3] bg-white">
                            <iframe
                                title="GTA Secure Inc. Service Area"
                                src="https://www.google.com/maps?q=Greater+Toronto+Area&output=embed"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="contact-map-iframe"
                            />
                        </div>
                    </div>

                    {/* Form */}
                    <div>
                        <div className="card-premium p-8">
                            <h2 className="font-display text-2xl md:text-3xl font-bold text-navy">
                                Send us a message
                            </h2>
                            <p className="mt-2 text-slate-600 text-sm">
                                We typically reply within one business day.
                            </p>
                            <form
                                onSubmit={onSubmit}
                                className="mt-6 space-y-4"
                                data-testid="contact-form"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="c-name" className="text-navy">Full name *</Label>
                                        <Input
                                            id="c-name"
                                            data-testid="contact-name-input"
                                            value={form.name}
                                            onChange={(e) => update("name", e.target.value)}
                                            required
                                            className="mt-1.5"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="c-phone" className="text-navy">Phone</Label>
                                        <Input
                                            id="c-phone"
                                            data-testid="contact-phone-input"
                                            value={form.phone}
                                            onChange={(e) => update("phone", e.target.value)}
                                            className="mt-1.5"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="c-email" className="text-navy">Email *</Label>
                                    <Input
                                        id="c-email"
                                        data-testid="contact-email-input"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => update("email", e.target.value)}
                                        required
                                        className="mt-1.5"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="c-subject" className="text-navy">Subject</Label>
                                    <Input
                                        id="c-subject"
                                        data-testid="contact-subject-input"
                                        value={form.subject}
                                        onChange={(e) => update("subject", e.target.value)}
                                        placeholder="How can we help?"
                                        className="mt-1.5"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="c-msg" className="text-navy">Message *</Label>
                                    <Textarea
                                        id="c-msg"
                                        data-testid="contact-message-input"
                                        value={form.message}
                                        onChange={(e) => update("message", e.target.value)}
                                        required
                                        rows={5}
                                        placeholder="Tell us about your project..."
                                        className="mt-1.5"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    data-testid="contact-submit-button"
                                    disabled={loading}
                                    className="btn-gold w-full disabled:opacity-70"
                                >
                                    {loading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Send className="h-4 w-4" />
                                    )}
                                    {loading ? "Sending..." : "Send Message"}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

function InfoItem({ icon: Icon, label, value, href, testid }) {
    const content = (
        <div className="flex items-start gap-4 card-premium p-5">
            <div className="h-11 w-11 rounded-xl bg-[#FDFCFB] border border-gold/30 text-gold flex items-center justify-center flex-shrink-0">
                <Icon className="h-5 w-5" />
            </div>
            <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#C9A961]">
                    {label}
                </p>
                <p className="mt-0.5 font-semibold text-navy">{value}</p>
            </div>
        </div>
    );
    return href ? (
        <a href={href} className="block" data-testid={testid}>
            {content}
        </a>
    ) : (
        <div data-testid={testid}>{content}</div>
    );
}
